import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import USPhoneInput from "../components/USPhoneInput";
import Footer from "../components/Footer";

// ─── Shared input styles ───────────────────────────────────────────────
const inputClass =
  "w-full font-constantia text-black-deep bg-white-pure border border-gray-200 rounded-lg px-4 py-2.5 text-sm leading-relaxed outline-none transition-all duration-200 placeholder-gray-400 focus:border-blue-primary focus:shadow-[0_0_0_3px_rgba(0,45,181,0.08)]";
const selectClass =
  "w-full font-constantia text-black-deep bg-white-pure border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none transition-all duration-200 focus:border-blue-primary focus:shadow-[0_0_0_3px_rgba(0,45,181,0.08)] cursor-pointer";
const labelClass = "block font-constantia text-black-deep mb-1.5";
const labelStyle = { fontSize: "14px", opacity: 0.8 };
const reqMark = <span className="text-red-500 ml-0.5">*</span>;

function SectionHeader({ title }) {
  return (
    <div className="flex items-center gap-2 mb-5">
      <div className="w-3 h-3 rounded-full bg-blue-primary flex-shrink-0" />
      <h3 className="font-cinzel font-bold text-blue-dark" style={{ fontSize: "15px", letterSpacing: "0.04em" }}>
        {title}
      </h3>
    </div>
  );
}

function FormCard({ children }) {
  return (
    <div className="bg-white-pure rounded-xl border border-gray-200 p-6 md:p-8 mb-5 shadow-sm">
      {children}
    </div>
  );
}

// ─── Step indicator ────────────────────────────────────────────────────
function StepBadge({ current, total }) {
  return (
    <div
      className="inline-flex items-center px-5 py-2 rounded-full font-constantia"
      style={{ backgroundColor: "rgba(1,46,114,0.07)", fontSize: "14px", color: "#012E72" }}
    >
      Step {current} of {total}
    </div>
  );
}

// ─── STEP 1 ────────────────────────────────────────────────────────────
const MARITAL_OPTIONS = ["Select marital status", "Single", "Married", "Divorced", "Widowed", "Domestic Partner"];
const GENDER_OPTIONS = ["Select gender", "Male", "Female", "Non-binary", "Prefer not to say"];
const LANGUAGE_OPTIONS = ["Select language", "English", "Spanish", "French", "Tagalog", "Vietnamese", "Other"];
const CANCEL_REASON_OPTIONS = [
  "Select reason",
  "Non-Payment",
  "Underwriting",
  "Policy Expired",
  "Switched Carriers",
  "No Longer Needed",
  "Other",
];

function Step1({ data, onChange, onAddClaim, onRemoveClaim, onClaimChange, claims, onNext }) {
  const field = (name, label, type = "text", placeholder = "", required = false, hint = null) => (
    <div>
      <label className={labelClass} style={labelStyle}>{label}{required && reqMark}</label>
      <input
        type={type}
        name={name}
        required={required}
        placeholder={placeholder}
        value={data[name] || ""}
        onChange={onChange}
        className={inputClass}
      />
      {hint && <p className="font-constantia mt-1 text-xs" style={{ color: "rgba(1,7,4,0.4)" }}>{hint}</p>}
    </div>
  );

  const select = (name, label, options, required = false) => (
    <div>
      <label className={labelClass} style={labelStyle}>{label}{required && reqMark}</label>
      <select name={name} value={data[name] || ""} onChange={onChange} className={selectClass}>
        {options.map((o) => <option key={o} value={o === options[0] ? "" : o}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <form onSubmit={onNext} className="flex flex-col gap-0">

      {/* Identity and Contact */}
      <FormCard>
        <SectionHeader title="Identity and Contact" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {field("firstName", "First Name", "text", "First name", true)}
          {field("middleName", "Middle Name", "text", "Middle name")}
          {field("lastName", "Last Name", "text", "Last name", true)}
          {field("dob", "Date of Birth", "date", "", true)}
          <USPhoneInput name="phone" value={data.phone || ""} onChange={onChange} label="Contact Phone Number" required />
          {field("email", "Email Address", "email", "name@example.com", true)}
        </div>
      </FormCard>

      {/* Mailing Address */}
      <FormCard>
        <SectionHeader title="Mailing Address" />
        <div className="flex flex-col gap-4">
          {field("street", "Street Address", "text", "Street address", true)}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("city", "City", "text", "City", true)}
            {field("state", "State", "text", "State", true)}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("zip", "ZIP Code", "text", "12345 or 12345-6789", true)}
            <div />
          </div>
        </div>
      </FormCard>

      {/* Identity Verification and Demographics */}
      <FormCard>
        <SectionHeader title="Identity Verification and Demographics" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {field("ssn4", "SSN (Last 4 Digits)", "text", "1234", true)}
          {field("ssnFull", "Full SSN (9 Digits)", "text", "123456789")}
          {select("maritalStatus", "Marital Status", MARITAL_OPTIONS)}
          {select("gender", "Gender", GENDER_OPTIONS)}
          {select("language", "Preferred Language", LANGUAGE_OPTIONS)}
          <div />
        </div>
      </FormCard>

      {/* Prior Insurance Information */}
      <FormCard>
        <SectionHeader title="Prior Insurance Information" />
        <div className="flex flex-col gap-4">
          {field("priorCarrier", "Prior Insurance Carrier (Most Recent)", "text", "Carrier name")}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("priorExpDate", "Prior Policy Expiration / Cancellation Date", "date")}
            {select("cancelReason", "Prior Policy Cancellation / Non-Renewal Reason", CANCEL_REASON_OPTIONS)}
          </div>
          {field("cancelDetails", "Reason Details", "text", "Add details if needed")}
        </div>
      </FormCard>

      {/* Loss / Claims History */}
      <FormCard>
        <SectionHeader title="Loss / Claims History (Past 5 Years)" />
        <p className="font-constantia mb-4" style={{ fontSize: "13px", color: "rgba(1,7,4,0.5)" }}>
          At least one claim row is required. Add more rows if applicable.
        </p>
        <div className="flex flex-col gap-3">
          {claims.map((claim, i) => (
            <div
              key={i}
              className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 rounded-lg border-l-4 border-blue-primary"
              style={{ backgroundColor: "rgba(1,46,114,0.03)", border: "1px solid rgba(1,46,114,0.12)", borderLeft: "4px solid #002DB5" }}
            >
              <div>
                <label className={labelClass} style={labelStyle}>Claim Date{reqMark}</label>
                <input type="date" className={inputClass} value={claim.date} onChange={(e) => onClaimChange(i, "date", e.target.value)} />
              </div>
              <div>
                <label className={labelClass} style={labelStyle}>Claim Type{reqMark}</label>
                <input type="text" className={inputClass} placeholder="Type of loss" value={claim.type} onChange={(e) => onClaimChange(i, "type", e.target.value)} />
              </div>
              <div>
                <label className={labelClass} style={labelStyle}>Paid Amount (USD){reqMark}</label>
                <div className="flex items-center gap-2">
                  <input type="number" className={inputClass} placeholder="1,000.00" value={claim.amount} onChange={(e) => onClaimChange(i, "amount", e.target.value)} />
                  {i > 0 && (
                    <button type="button" onClick={() => onRemoveClaim(i)} className="text-red-400 hover:text-red-600 flex-shrink-0 font-cinzel text-xs px-2 py-1 rounded border border-red-200 hover:border-red-400 transition-colors">
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={onAddClaim}
          className="mt-4 font-cinzel uppercase tracking-widest text-blue-dark border border-blue-dark border-opacity-30 px-5 py-2 rounded-lg hover:bg-blue-dark hover:text-white-pure transition-colors duration-200"
          style={{ fontSize: "11px", letterSpacing: "0.15em" }}
        >
          + Add Another Claim
        </button>
      </FormCard>

      {/* Consent and Signature */}
      <FormCard>
        <SectionHeader title="Consent and Signature" />
        <div className="flex flex-col gap-4">
          {[
            { name: "consentCredit", label: "I consent to a soft credit / insurance score inquiry." },
            { name: "consentDelivery", label: "I consent to electronic delivery of documents." },
          ].map(({ name, label }) => (
            <label
              key={name}
              className="flex items-start gap-3 cursor-pointer p-4 rounded-lg border transition-colors duration-200 hover:border-blue-primary"
              style={{ borderColor: data[name] ? "rgba(0,45,181,0.4)" : "rgba(0,0,0,0.1)", backgroundColor: data[name] ? "rgba(0,45,181,0.04)" : "transparent" }}
            >
              <input
                type="checkbox"
                name={name}
                checked={!!data[name]}
                onChange={onChange}
                className="mt-0.5 w-4 h-4 accent-blue-dark flex-shrink-0"
              />
              <span className="font-constantia text-black-deep" style={{ fontSize: "14px" }}>
                {label} {reqMark}
              </span>
            </label>
          ))}

          <div>
            <label className={labelClass} style={labelStyle}>Electronic Signature (Type Full Name){reqMark}</label>
            <input type="text" name="signature" required placeholder="Type your full legal name" value={data.signature || ""} onChange={onChange} className={inputClass} />
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>Referral Code</label>
            <input type="text" name="referral" placeholder="Enter referral code (optional)" value={data.referral || ""} onChange={onChange} className={inputClass} />
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>Additional Notes</label>
            <textarea name="notes" rows={4} placeholder="Anything else we should know before we quote?" value={data.notes || ""} onChange={onChange} className={`${inputClass} resize-none`} />
          </div>
        </div>
      </FormCard>

      {/* Submit Step 1 */}
      <button
        type="submit"
        className="w-full bg-blue-dark text-white-pure font-cinzel uppercase tracking-widest py-4 rounded-xl hover:bg-blue-primary transition-colors duration-300 flex items-center justify-center gap-3 mt-2"
        style={{ fontSize: "13px", letterSpacing: "0.18em" }}
      >
        Continue to Insurance Selection
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
        </svg>
      </button>
    </form>
  );
}

// ─── STEP 2 ────────────────────────────────────────────────────────────
const INSURANCE_TYPES = [
  { id: "general-liability", label: "General Liability" },
  { id: "professional-liability", label: "Professional Liability (E&O)" },
  { id: "workers-comp", label: "Worker's Compensation" },
  { id: "homeowners", label: "Homeowners" },
  { id: "personal-auto", label: "Personal Auto" },
  { id: "commercial-auto", label: "Commercial Auto" },
  { id: "business", label: "Business" },
  { id: "renters", label: "Renters" },
  { id: "umbrella", label: "Umbrella" },
  { id: "flood", label: "Flood" },
  { id: "earthquake", label: "Earthquake" },
  { id: "special-event", label: "Special Event" },
];

function Step2({ selected, onToggle, onNext, onBack }) {
  return (
    <div>
      <FormCard>
        <SectionHeader title="Select Your Insurance Type(s)" />
        <p className="font-constantia mb-6" style={{ fontSize: "14px", color: "rgba(1,7,4,0.55)" }}>
          Select all that apply. Our agents will tailor a quote based on your selections.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {INSURANCE_TYPES.map(({ id, label, icon }) => {
            const active = selected.includes(id);
            return (
              <button
                key={id}
                type="button"
                onClick={() => onToggle(id)}
                className="flex items-center gap-1 px-4 py-3.5 rounded-xl border text-left transition-all duration-200 hover:-translate-y-0.5"
                style={{
                  borderColor: active ? "#002DB5" : "rgba(1,46,114,0.15)",
                  backgroundColor: active ? "rgba(0,45,181,0.06)" : "#FFFFFF",
                  boxShadow: active ? "0 0 0 2px rgba(0,45,181,0.2)" : "none",
                }}
              >
                <span style={{ fontSize: "20px" }}>{icon}</span>
                <span className="font-constantia text-blue-dark" style={{ fontSize: "14px", fontWeight: active ? 600 : 400 }}>
                  {label}
                </span>
                {active && (
                  <div className="ml-auto w-4 h-4 rounded-full bg-blue-dark flex items-center justify-center flex-shrink-0">
                    <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                      <polyline points="1.5 5 4 7.5 8.5 2.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </FormCard>

      <div className="flex gap-4 mt-2">
        <button type="button" onClick={onBack}
          className="flex-1 font-cinzel uppercase tracking-widest text-blue-dark border border-blue-dark border-opacity-30 py-4 rounded-xl hover:bg-blue-dark hover:text-white-pure transition-colors duration-200"
          style={{ fontSize: "12px" }}>
          ← Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={selected.length === 0}
          className="flex-[3] bg-blue-dark text-white-pure font-cinzel uppercase tracking-widest py-4 rounded-xl hover:bg-blue-primary transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-3"
          style={{ fontSize: "13px", letterSpacing: "0.18em" }}>
          Review & Submit →
        </button>
      </div>
    </div>
  );
}

// ─── STEP 3 ────────────────────────────────────────────────────────────
function Step3({ data, selected, onBack, onSubmit, submitted, submitting, submitError }) {
  if (submitted) {
    return (
      <FormCard>
        <div className="flex flex-col items-center text-center gap-5 py-10">
          <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(1,46,114,0.08)" }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h3 className="font-cinzel font-bold text-blue-dark uppercase" style={{ fontSize: "18px", letterSpacing: "0.08em" }}>
            Request Submitted!
          </h3>
          <p className="font-constantia text-black-deep max-w-md leading-relaxed" style={{ fontSize: "15px", opacity: 0.65 }}>
            Thank you, <strong>{data.firstName}</strong>. Our licensed agents will review your submission and reach out within 1–3 business days with your quote or to request any additional information needed to complete it.
          </p>
          <div className="h-px w-24 bg-blue-dark opacity-15" />
          <p className="font-constantia italic" style={{ fontSize: "13px", color: "rgba(1,7,4,0.4)" }}>
            A confirmation will be sent to <strong>{data.email}</strong>
          </p>
        </div>
      </FormCard>
    );
  }

  const ReviewRow = ({ label, value }) => value ? (
    <div className="flex justify-between items-start gap-4 py-2 border-b last:border-0" style={{ borderColor: "rgba(1,46,114,0.07)" }}>
      <span className="font-cinzel text-blue-dark uppercase flex-shrink-0" style={{ fontSize: "10px", letterSpacing: "0.15em", opacity: 0.55 }}>{label}</span>
      <span className="font-constantia text-black-deep text-right" style={{ fontSize: "14px", opacity: 0.8 }}>{value}</span>
    </div>
  ) : null;

  return (
    <div>
      <FormCard>
        <SectionHeader title="Review Your Information" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="font-cinzel uppercase tracking-widest mb-3" style={{ fontSize: "10px", color: "rgba(1,46,114,0.5)" }}>Personal Info</p>
            <ReviewRow label="Name" value={`${data.firstName || ""} ${data.middleName || ""} ${data.lastName || ""}`.trim()} />
            <ReviewRow label="Date of Birth" value={data.dob} />
            <ReviewRow label="Phone" value={data.phone} />
            <ReviewRow label="Email" value={data.email} />
          </div>
          <div>
            <p className="font-cinzel uppercase tracking-widest mb-3" style={{ fontSize: "10px", color: "rgba(1,46,114,0.5)" }}>Address</p>
            <ReviewRow label="Street" value={data.street} />
            <ReviewRow label="City / State" value={data.city && data.state ? `${data.city}, ${data.state} ${data.zip}` : ""} />
          </div>
        </div>
        <div className="mt-6">
          <p className="font-cinzel uppercase tracking-widest mb-3" style={{ fontSize: "10px", color: "rgba(1,46,114,0.5)" }}>Selected Insurance Lines</p>
          <div className="flex flex-wrap gap-2">
            {selected.map((id) => {
              const ins = INSURANCE_TYPES.find((i) => i.id === id);
              return (
                <span key={id} className="font-constantia text-blue-dark px-3 py-1 rounded-full" style={{ fontSize: "13px", backgroundColor: "rgba(0,45,181,0.07)", border: "1px solid rgba(0,45,181,0.15)" }}>
                  {ins?.icon} {ins?.label}
                </span>
              );
            })}
          </div>
        </div>
      </FormCard>

      {/* Confirmation note */}
      <div className="rounded-xl p-5 mb-5" style={{ backgroundColor: "rgba(1,46,114,0.04)", border: "1px solid rgba(1,46,114,0.12)" }}>
        <p className="font-constantia text-black-deep leading-relaxed" style={{ fontSize: "14px", opacity: 0.65 }}>
          By submitting, you confirm that all information is accurate and that you have read and agreed to the consent items in Step 1. Review and initial follow-up typically take 1–3 business days.
        </p>
      </div>

      {submitError && (
        <div className="mb-4 px-5 py-3 rounded-lg bg-red-50 border border-red-200">
          <p className="font-constantia text-red-700 text-sm">{submitError}</p>
        </div>
      )}

      <div className="flex gap-4">
        <button type="button" onClick={onBack} disabled={submitting}
          className="flex-1 font-cinzel uppercase tracking-widest text-blue-dark border border-blue-dark border-opacity-30 py-4 rounded-xl hover:bg-blue-dark hover:text-white-pure transition-colors duration-200 disabled:opacity-40"
          style={{ fontSize: "12px" }}>
          ← Back
        </button>
        <button type="button" onClick={onSubmit} disabled={submitting}
          className="flex-[3] bg-blue-dark text-white-pure font-cinzel uppercase tracking-widest py-4 rounded-xl hover:bg-blue-primary transition-colors duration-300 flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ fontSize: "13px", letterSpacing: "0.15em" }}>
          {submitting ? "Submitting..." : "Submit Quote Request"}
          {!submitting && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── CONSULTATION MODAL ────────────────────────────────────────────────
function ConsultationModal({ onClose }) {
  const [modalStep, setModalStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ fullName: "", email: "", address: "", phone: "", details: "" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/consultations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Submission failed.");
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const modalInput =
    "w-full font-constantia text-black-deep bg-white-pure border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none transition-all duration-200 placeholder-gray-400 focus:border-blue-primary focus:shadow-[0_0_0_3px_rgba(0,45,181,0.08)]";
  const modalLabel = "block font-constantia text-black-deep mb-1.5 text-sm";

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Modal card */}
      <div className="bg-white-pure rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="px-8 pt-7 pb-5 border-b border-gray-100">
          <div className="flex items-start justify-between gap-4">
            <div>
              <span
                className="inline-block font-cinzel uppercase tracking-widest border border-blue-dark border-opacity-30 text-blue-dark px-3 py-1 rounded-full mb-3"
                style={{ fontSize: "10px", letterSpacing: "0.2em" }}
              >
                Consultation
              </span>
              <h2 className="font-cinzel font-bold text-blue-dark uppercase" style={{ fontSize: "20px", letterSpacing: "0.06em" }}>
                Personalized Consultation
              </h2>
              <p className="font-constantia text-black-deep mt-1" style={{ fontSize: "14px", opacity: 0.55 }}>
                Use this form to share your contact details. We will review it and follow up within 3–4 business days.
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:border-blue-dark hover:text-blue-dark transition-colors flex-shrink-0 mt-1"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-3 mt-5 p-4 rounded-xl" style={{ backgroundColor: "#F7F9FF" }}>
            {[
              {
                step: 1,
                icon: (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                  </svg>
                ),
                label: "Step 1",
              },
              {
                step: 2,
                icon: (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                ),
                label: "Step 2",
              },
            ].map(({ step, icon, label }, i) => {
              const active = modalStep === step;
              const done = modalStep > step;
              return (
                <div key={step} className="flex items-center gap-3 flex-1">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300"
                    style={{
                      backgroundColor: done ? "#012E72" : active ? "#002DB5" : "rgba(1,46,114,0.08)",
                      color: done || active ? "#FFFFFF" : "rgba(1,46,114,0.35)",
                    }}
                  >
                    {done ? (
                      <svg width="14" height="14" viewBox="0 0 10 10" fill="none">
                        <polyline points="1.5 5 4 7.5 8.5 2.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : icon}
                  </div>
                  <span
                    className="font-cinzel uppercase tracking-widest"
                    style={{ fontSize: "11px", color: active ? "#012E72" : "rgba(1,46,114,0.35)", letterSpacing: "0.15em" }}
                  >
                    {label}
                  </span>
                  {i === 0 && (
                    <div className="flex-1 h-px mx-2" style={{ backgroundColor: modalStep > 1 ? "#012E72" : "rgba(1,46,114,0.15)" }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="px-8 py-6 overflow-y-auto flex-1">

          {/* Note */}
          <div className="flex items-start gap-3 px-4 py-3 rounded-lg mb-6" style={{ backgroundColor: "#FFFBEB", border: "1px solid #FDE68A" }}>
            <span style={{ fontSize: "14px" }}>📝</span>
            <p className="font-constantia text-sm" style={{ color: "#92400E" }}>
              <strong>Note:</strong> This process will take 3–4 business days.
            </p>
          </div>

          {submitted ? (
            /* Success state */
            <div className="flex flex-col items-center text-center gap-4 py-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(1,46,114,0.08)" }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h3 className="font-cinzel font-bold text-blue-dark uppercase" style={{ fontSize: "16px", letterSpacing: "0.08em" }}>
                Request Received!
              </h3>
              <p className="font-constantia text-black-deep max-w-sm leading-relaxed" style={{ fontSize: "15px", opacity: 0.65 }}>
                Thank you, <strong>{form.fullName}</strong>. One of our licensed agents will follow up with you within 3–4 business days.
              </p>
              <button
                onClick={onClose}
                className="font-cinzel uppercase tracking-widest bg-blue-dark text-white-pure px-8 py-3 rounded-full hover:bg-blue-primary transition-colors duration-200 mt-2"
                style={{ fontSize: "11px" }}
              >
                Close
              </button>
            </div>
          ) : modalStep === 1 ? (
            /* Step 1 — Contact details */
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={modalLabel}>Full name <span className="text-red-500">*</span></label>
                  <input type="text" name="fullName" required placeholder="John Doe" value={form.fullName} onChange={handleChange} className={modalInput} />
                </div>
                <div>
                  <label className={modalLabel}>Email address <span className="text-red-500">*</span></label>
                  <input type="email" name="email" required placeholder="john@example.com" value={form.email} onChange={handleChange} className={modalInput} />
                </div>
                <div>
                  <label className={modalLabel}>Address <span className="text-red-500">*</span></label>
                  <input type="text" name="address" required placeholder="Street address" value={form.address} onChange={handleChange} className={modalInput} />
                </div>
                <USPhoneInput
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  label="Phone number"
                  required
                />
              </div>
              <div>
                <label className={modalLabel}>Details on consultation <span className="font-constantia" style={{ fontSize: "12px", opacity: 0.5 }}>(optional)</span></label>
                <textarea
                  name="details" rows={5} placeholder="Share any additional context for your consultation request"
                  value={form.details} onChange={handleChange}
                  className={`${modalInput} resize-none`}
                />
              </div>
            </div>
          ) : (
            /* Step 2 — Review */
            <div className="flex flex-col gap-4">
              <p className="font-constantia text-black-deep mb-2" style={{ fontSize: "14px", opacity: 0.6 }}>
                Please review your details before submitting your consultation request.
              </p>
              <div className="rounded-xl border border-gray-200 overflow-hidden">
                {[
                  { label: "Full Name", value: form.fullName },
                  { label: "Email", value: form.email },
                  { label: "Address", value: form.address },
                  { label: "Phone", value: form.phone },
                  { label: "Details", value: form.details || "—" },
                ].map(({ label, value }) => (
                  <div key={label} className="flex items-start gap-4 px-5 py-3 border-b last:border-0" style={{ borderColor: "rgba(1,46,114,0.07)" }}>
                    <span className="font-cinzel uppercase flex-shrink-0 w-24" style={{ fontSize: "9px", letterSpacing: "0.15em", color: "rgba(1,46,114,0.5)", paddingTop: "2px" }}>
                      {label}
                    </span>
                    <span className="font-constantia text-black-deep" style={{ fontSize: "14px", opacity: 0.8 }}>{value}</span>
                  </div>
                ))}
              </div>
              {error && (
                <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200">
                  <p className="font-constantia text-red-700 text-sm">{error}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!submitted && (
          <div className="px-8 py-5 border-t border-gray-100 flex items-center justify-between">
            <button
              onClick={onClose}
              className="font-cinzel uppercase tracking-widest text-black-deep border border-gray-200 px-6 py-2.5 rounded-full hover:border-blue-dark hover:text-blue-dark transition-colors duration-200"
              style={{ fontSize: "11px" }}
            >
              Close
            </button>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setModalStep(1)}
                disabled={modalStep === 1}
                className="font-cinzel uppercase tracking-widest text-blue-dark border border-blue-dark border-opacity-30 px-6 py-2.5 rounded-full hover:bg-blue-dark hover:text-white-pure transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ fontSize: "11px" }}
              >
                Previous
              </button>
              {modalStep === 1 ? (
                <button
                  onClick={() => {
                    if (!form.fullName || !form.email || !form.address || !form.phone) return;
                    setModalStep(2);
                  }}
                  disabled={!form.fullName || !form.email || !form.address || !form.phone}
                  className="font-cinzel uppercase tracking-widest bg-blue-dark text-white-pure px-8 py-2.5 rounded-full hover:bg-blue-primary transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ fontSize: "11px" }}
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="font-cinzel uppercase tracking-widest bg-blue-dark text-white-pure px-8 py-2.5 rounded-full hover:bg-blue-primary transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ fontSize: "11px" }}
                >
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN PAGE ─────────────────────────────────────────────────────────
export default function QuotePage() {
  usePageTitle("Get a Quote");
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [showConsultation, setShowConsultation] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [form, setForm] = useState({});
  const [claims, setClaims] = useState([{ date: "", type: "", amount: "" }]);
  const [selectedInsurance, setSelectedInsurance] = useState([]);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleClaimChange = (i, field, value) => {
    setClaims((prev) => prev.map((c, idx) => idx === i ? { ...c, [field]: value } : c));
  };

  const handleAddClaim = () => setClaims((prev) => [...prev, { date: "", type: "", amount: "" }]);
  const handleRemoveClaim = (i) => setClaims((prev) => prev.filter((_, idx) => idx !== i));

  const handleToggleInsurance = (id) => {
    setSelectedInsurance((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleFinalSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = {
        ...form,
        claims,
        selectedInsurance,
        consentCredit: !!form.consentCredit,
        consentDelivery: !!form.consentDelivery,
      };
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/quotes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Submission failed.");
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const STEP_LABELS = ["Universal Form", "Insurance Selection", "Review & Submit"];

  return (
    <>
    {showConsultation && <ConsultationModal onClose={() => setShowConsultation(false)} />}
    <main className="w-full min-h-screen" style={{ backgroundColor: "#F0F4FA" }}>

      {/* ── Page Header ── */}
      <section className="w-full bg-white-pure border-b border-gray-100 py-12 px-6 text-center">
        <p className="font-cinzel text-blue-primary uppercase tracking-widest mb-3" style={{ fontSize: "11px", letterSpacing: "0.3em" }}>
          Paladin Professional Insurance Solutions
        </p>
        <h1 className="font-cinzel font-bold text-blue-dark uppercase mb-3" style={{ fontSize: "clamp(26px, 4vw, 44px)", letterSpacing: "0.06em" }}>
          Request a Quote
        </h1>
        <p className="font-constantia text-black-deep mb-5" style={{ fontSize: "16px", opacity: 0.55 }}>
          Complete the universal applicant form first, then continue to your insurance-specific form.
        </p>
        <StepBadge current={step} total={3} />
        <p className="font-constantia mt-4" style={{ fontSize: "13px", color: "rgba(1,7,4,0.45)" }}>
          Have no time filling out this form?{" "}
          <button onClick={() => setShowConsultation(true)} className="text-blue-dark underline hover:text-blue-primary transition-colors">
            Start a Personalized Consultation
          </button>{" "}
          instead.
        </p>

        {/* Progress bar */}
        <div className="max-w-xl mx-auto mt-8 flex items-center gap-0">
          {STEP_LABELS.map((label, i) => {
            const num = i + 1;
            const done = step > num;
            const active = step === num;
            return (
              <div key={label} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center font-cinzel font-bold transition-all duration-300"
                    style={{
                      fontSize: "12px",
                      backgroundColor: done ? "#012E72" : active ? "#002DB5" : "rgba(1,46,114,0.1)",
                      color: done || active ? "#FFFFFF" : "rgba(1,46,114,0.4)",
                    }}
                  >
                    {done ? (
                      <svg width="12" height="12" viewBox="0 0 10 10" fill="none">
                        <polyline points="1.5 5 4 7.5 8.5 2.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : num}
                  </div>
                  <span className="font-cinzel uppercase mt-1.5 hidden sm:block" style={{ fontSize: "9px", letterSpacing: "0.1em", color: active ? "#012E72" : "rgba(1,46,114,0.35)", whiteSpace: "nowrap" }}>
                    {label}
                  </span>
                </div>
                {i < STEP_LABELS.length - 1 && (
                  <div className="flex-1 h-px mx-2 transition-colors duration-300" style={{ backgroundColor: step > num + 0 ? "#012E72" : "rgba(1,46,114,0.15)" }} />
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Form body ── */}
      <div className="max-w-3xl mx-auto px-4 py-10">

        {/* Form card header */}
        {!submitted && (
          <div className="bg-white-pure rounded-xl border border-gray-200 px-8 py-5 mb-5 shadow-sm">
            <h2 className="font-cinzel font-bold text-blue-dark" style={{ fontSize: "17px", letterSpacing: "0.06em" }}>
              {step === 1 && "Universal Applicant Form"}
              {step === 2 && "Insurance Selection"}
              {step === 3 && "Review & Submit"}
            </h2>
            <p className="font-constantia mt-1" style={{ fontSize: "13px", color: "rgba(1,7,4,0.45)" }}>
              {step === 1 && "Please complete all required fields and as many optional fields as possible to reduce follow-up calls."}
              {step === 2 && "Select the insurance lines you'd like a quote for."}
              {step === 3 && "Review your information before final submission."}
            </p>
            {step === 1 && (
              <p className="font-constantia italic mt-1" style={{ fontSize: "12px", color: "rgba(1,7,4,0.4)" }}>
                Note: Review and initial follow-up typically take 1–3 business days.
              </p>
            )}
          </div>
        )}

        {step === 1 && (
          <Step1
            data={form}
            onChange={handleFormChange}
            claims={claims}
            onClaimChange={handleClaimChange}
            onAddClaim={handleAddClaim}
            onRemoveClaim={handleRemoveClaim}
            onNext={(e) => { e.preventDefault(); setStep(2); window.scrollTo(0, 0); }}
          />
        )}

        {step === 2 && (
          <Step2
            selected={selectedInsurance}
            onToggle={handleToggleInsurance}
            onNext={() => { setStep(3); window.scrollTo(0, 0); }}
            onBack={() => { setStep(1); window.scrollTo(0, 0); }}
          />
        )}

        {step === 3 && (
          <Step3
            data={form}
            selected={selectedInsurance}
            submitted={submitted}
            submitting={submitting}
            submitError={submitError}
            onBack={() => { setStep(2); window.scrollTo(0, 0); }}
            onSubmit={handleFinalSubmit}
          />
        )}
      </div>

      {/* ── Support footer bar ── */}
      <div className="w-full py-8 px-6" style={{ backgroundColor: "#012E72" }}>
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="font-cinzel font-bold text-white-pure" style={{ fontSize: "14px", letterSpacing: "0.06em" }}>
              Need Support?
            </p>
            <p className="font-constantia text-white-pure mt-1" style={{ fontSize: "14px", opacity: 0.6 }}>
              Have a question or need assistance? Our team is here to help you find the right insurance solution.
            </p>
          </div>
          <button
            onClick={() => navigate("/contact")}
            className="font-cinzel uppercase tracking-widest text-blue-dark bg-white-pure px-6 py-3 rounded-full hover:bg-white-warm transition-colors duration-200 flex-shrink-0"
            style={{ fontSize: "11px", letterSpacing: "0.15em" }}
          >
            Contact Support
          </button>
        </div>
      </div>

    </main>
    <Footer />
    </>
  );
}
